import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GoogleButton } from "@/pages/LoginPage";
import { CREDIT_HELP } from "@/components/Billing";

export function AuthCard({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid place-items-center p-6 lab-grid">
      <div className="w-full max-w-md bg-card border rounded-2xl p-8 shadow-sm rise">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground grid place-items-center">
            <img
              src="/askjonnylogosmall.png"
              alt="Ask Jonny"
              className="h-full w-full rounded-xl object-contain bg-white"
              data-testid="auth-card-brand-logo"
            />
          </div>
          <div>
            <div className="font-bold text-lg leading-none">{title}</div>
            <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

const PERKS = [
  "14-day free trial, no card needed",
  "5 AI credits to try Jonny",
  "You become your school's admin and can invite staff",
];

export default function SignupPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({
    school_name: "",
    name: "",
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/" replace />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register-school", f);
      setUser(data);
      navigate("/chat");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const beforeGoogle = () => {
    sessionStorage.removeItem("aj-invite-token");
    if (f.school_name.trim().length < 3) {
      setError("Enter your school name first, then continue with Google.");
      return false;
    }
    sessionStorage.setItem("aj-signup-school", f.school_name.trim());
    return true;
  };

  return (
    <AuthCard
      title="Register your school"
      subtitle="Set up Ask Jonny for your science department"
    >
      <ul className="space-y-1 mb-5 text-sm">
        {PERKS.map((p) => (
          <li key={p} className="flex gap-2">
            <Check className="h-4 w-4 text-primary mt-0.5" />
            {p}
          </li>
        ))}
      </ul>
      <form onSubmit={submit} className="space-y-3.5" data-testid="signup-form">
        <div className="space-y-1.5">
          <Label htmlFor="school">School name</Label>
          <Input
            id="school"
            value={f.school_name}
            onChange={set("school_name")}
            required
            minLength={3}
            data-testid="signup-school-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="name">Your name</Label>
          <Input
            id="name"
            value={f.name}
            onChange={set("name")}
            required
            autoComplete="name"
            data-testid="signup-name-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="semail">Work email</Label>
          <Input
            id="semail"
            type="email"
            value={f.email}
            onChange={set("email")}
            required
            autoComplete="email"
            data-testid="signup-email-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="spw">Password</Label>
          <Input
            id="spw"
            type="password"
            value={f.password}
            onChange={set("password")}
            required
            minLength={8}
            autoComplete="new-password"
            data-testid="signup-password-input"
          />
          <p className="text-[11px] text-muted-foreground">
            At least 8 characters.
          </p>
        </div>
        {error && (
          <p className="text-sm text-destructive" data-testid="signup-error">
            {error}
          </p>
        )}
        <Button
          type="submit"
          className="w-full h-11"
          disabled={busy}
          data-testid="signup-submit-button"
        >
          {busy ? "Creating your school…" : "Start free trial"}{" "}
          <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </form>
      <div className="flex items-center gap-3 my-4 text-xs text-muted-foreground">
        <div className="h-px bg-border flex-1" />
        or
        <div className="h-px bg-border flex-1" />
      </div>
      <GoogleButton
        before={beforeGoogle}
        label="Register with Google"
        testId="signup-google-button"
      />
      <p className="text-[11px] text-muted-foreground mt-4">{CREDIT_HELP}</p>
      <p className="text-xs mt-4">
        Already using Ask Jonny?{" "}
        <Link
          to="/login"
          className="text-primary hover:underline"
          data-testid="signup-login-link"
        >
          Sign in
        </Link>
      </p>
    </AuthCard>
  );
}
